import React from 'react';

import MUILink from '@mui/material/Link';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';

import { Link } from '../Link';
import { Icon } from '../Icon';
import { Box } from '../Box';
import { FilePreviewWithZoom } from '../FilePreviewWithZoom';
import { Dialog } from '../Dialog';
import { useIsDesktop } from '../hooks';

import {
  PreviewImage,
  PreviewPdf,
  PreviewDocx,
  PreviewSnackbar,
} from './Previews';
import { useStyles } from './FilePreviewWrapper.styles';
import { FilePreviewContentProps } from './FilePreview.types';

export const FilePreviewContent = ({
  shape = 'auto',
  onClickPrint,
  onClickDownload,
  onLoadSuccess,
  withCollapsableFooter,
  view = 'file',
  showSnackbarOnDesktop = false,
  snackbarMessage,
  snackbarAction,
  linkText,
  withFlex = false,
  centerImage = false,
  withZoom = false,
  blob,
  blobType,
  objectUrl,
  onLoadFull,
  handleOpen,
  handleClose,
  clickable,
  open,
}: FilePreviewContentProps) => {
  const { classes } = useStyles({ mb: {}, mt: {} });
  const isDesktop = useIsDesktop();

  if (blobType === 'application/pdf') {
    const links =
      view === 'fileLarge' ? (
        <Box
          p="24"
          py="16"
          bgcolor="bg.greyscale.primary"
          radius="l"
          maxHeight={400}
          overflow="hidden"
          position="relative"
          className="aqa_file_preview_pdf_wrapper"
        >
          <PreviewPdf
            url={objectUrl}
            shape={shape}
            onClickDownload={onClickDownload}
            onLoadSuccess={onLoadSuccess}
          />
          <MUILink onClick={handleOpen}>
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              bgcolor="bg.dark.primary"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p="12"
            >
              <Box color="bg.light.primary">{linkText}</Box>
              <Box color="bg.light.primary">
                <ArrowForwardIos fontSize="small" />
              </Box>
            </Box>
          </MUILink>
        </Box>
      ) : (
        <Link onClick={handleOpen} stroke={false} size="small">
          {linkText || 'Просмотр'}
        </Link>
      );

    return clickable ? (
      <>
        {view === 'file' ? (
          <Box
            className={`${classes.documentCursor} aqa_file_preview_pdf_wrapper`}
          >
            <PreviewPdf
              url={objectUrl}
              shape={shape}
              onClickPrint={onClickPrint}
              onOpen={handleOpen}
              onClickDownload={onClickDownload}
              withCollapsableFooter={withCollapsableFooter}
              onLoadSuccess={onLoadSuccess}
            />
          </Box>
        ) : (
          links
        )}
        {isDesktop && withZoom ? (
          <FilePreviewWithZoom
            onClose={handleClose}
            open={open}
            url={objectUrl}
            onLoadSuccess={onLoadSuccess}
            withBottomOffset={showSnackbarOnDesktop}
          />
        ) : (
          <Dialog
            scroll="body"
            onClose={handleClose}
            open={open}
            fullScreen={!isDesktop}
            className="aqa_file_preview_dialog"
            classes={{
              paper: classes.dialogPaper,
            }}
            withoutRadius={!isDesktop}
          >
            <PreviewPdf
              url={objectUrl}
              shape="auto"
              onLoadSuccess={onLoadSuccess}
              onClickDownload={!isDesktop ? onClickDownload : undefined}
            />
          </Dialog>
        )}
        {(showSnackbarOnDesktop || !isDesktop) && (
          <PreviewSnackbar
            open={open}
            onClick={handleClose}
            message={snackbarMessage}
            action={snackbarAction}
          />
        )}
      </>
    ) : (
      <Box>
        <PreviewPdf
          url={objectUrl}
          shape={shape}
          onClickPrint={onClickPrint}
          onClickDownload={onClickDownload}
          withCollapsableFooter={withCollapsableFooter}
          onLoadSuccess={onLoadSuccess}
          {...(withZoom && { onExpand: handleOpen })}
        />
        {withZoom && isDesktop && (
          <FilePreviewWithZoom
            onClose={handleClose}
            open={open}
            url={objectUrl}
            onLoadSuccess={onLoadSuccess}
            withBottomOffset={showSnackbarOnDesktop}
          />
        )}
      </Box>
    );
  }

  if (blobType?.startsWith('image/')) {
    const renderImage = () => (
      <>
        <PreviewImage
          url={objectUrl}
          shape={shape}
          onLoadFull={onLoadFull}
          clickable={!!clickable}
          onClickDownload={onClickDownload}
          withCollapsableFooter={withCollapsableFooter}
          centerImage={centerImage}
          onLoadSuccess={onLoadSuccess}
          {...(withZoom && { onExpand: handleOpen })}
        />
        {withZoom && (
          <FilePreviewWithZoom
            onClose={handleClose}
            open={open}
            url={objectUrl}
            onLoadFull={onLoadFull}
            onLoadSuccess={onLoadSuccess}
            withBottomOffset={showSnackbarOnDesktop}
            fileType="image"
          />
        )}
      </>
    );

    return withFlex ? (
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {renderImage()}
      </Box>
    ) : (
      renderImage()
    );
  }

  if (blobType === 'text/xml' || blobType === 'application/xml') {
    return (
      <Box>
        <Box
          px="32"
          py="48"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon
            name="vkhrtek_xml_file"
            color="original.brand.primary"
            size="48"
          />
        </Box>
      </Box>
    );
  }

  if (
    blobType === 'application/msword' ||
    blobType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return (
      <PreviewDocx
        shape={shape}
        onClickDownload={onClickDownload}
        withCollapsableFooter={withCollapsableFooter}
      />
    );
  }

  if (blob?.path?.endsWith('.sig')) {
    return (
      <Box>
        <Box
          px="32"
          py="48"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon
            name="vkhrtek_sig_file"
            color="original.brand.primary"
            size="48"
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      p="32"
      color="text.light.secondary"
      textAlign="center"
      className="aqa_file_preview_unsupported"
    >
      Неподдерживаемый тип файла {blobType || blob?.type}
    </Box>
  );
};
